'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import { JWTUserData } from '@kit/supabase/types';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface RSSHeaderAccountSectionProps {
  onNavigate?: () => void;
  usesDarkText?: boolean;
}

export function RSSHeaderAccountSection({ onNavigate, usesDarkText = false }: RSSHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  // If user is authenticated
  if (session.data) {
    const role = userRole.data || 'guest';

    // Owner/Member roles - show admin access
    if (role === 'owner' || role === 'member') {
      return (
        <div className={'animate-in fade-in flex flex-wrap items-center gap-x-4 gap-y-3 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          {/* View Classes - Primary CTA (Red fill) */}
          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex items-center justify-center bg-[#ED253E] text-white text-sm font-semibold tracking-[1px] uppercase h-10 px-6 hover:bg-[#BE2030] transition-colors duration-200"
          >
            <Trans i18nKey={'common:bookCourt'} defaults="BOOK NOW" />
          </Link>

          {/* Admin Panel - Secondary (Outline) */}
          <Link
            href={teamAccount.data ? `/home/${teamAccount.data.slug}/dashboard` : '/home/settings'}
            onClick={onNavigate}
            className="inline-flex items-center justify-center border-2 border-white text-white text-sm font-semibold tracking-[1px] uppercase h-10 px-6 hover:bg-white/10 transition-colors duration-200"
          >
            <Trans i18nKey={'common:adminPanel'} defaults="ADMIN PANEL" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className='z-50'
          />
        </div>
      );
    }

    // User role - show profile only (no admin access)
    if (role === 'user') {
      return (
        <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          {/* View Classes - Primary CTA (Red fill) */}
          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex items-center justify-center bg-[#ED253E] text-white text-sm font-semibold tracking-[1px] uppercase h-10 px-6 hover:bg-[#BE2030] transition-colors duration-200"
          >
            <Trans i18nKey={'common:bookCourt'} defaults="BOOK NOW" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className='z-50'
          />
        </div>
      );
    }
  }

  // Guest users - show auth buttons
  return <AuthButtons onNavigate={onNavigate} usesDarkText={usesDarkText} />;
}

function AuthButtons({ onNavigate, usesDarkText = false }: { onNavigate?: () => void; usesDarkText?: boolean }) {
  return (
    <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
      <div className={'hidden md:flex'}>
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      {/* View Classes - Primary CTA (Red fill) */}
      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="inline-flex items-center justify-center bg-[#ED253E] text-white text-sm font-semibold tracking-[1px] uppercase h-10 px-6 hover:bg-[#BE2030] transition-colors duration-200"
      >
        <Trans i18nKey={'common:bookCourt'} defaults="BOOK NOW" />
      </Link>

      {/* Sign In - Secondary (Outline) */}
      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className={`inline-flex items-center justify-center border-2 text-sm font-semibold tracking-[1px] uppercase h-10 px-6 transition-colors duration-200 ${
          usesDarkText
            ? 'border-[#293044] text-[#293044] hover:bg-[#293044]/10'
            : 'border-white text-white hover:bg-white/10'
        }`}
      >
        <Trans i18nKey={'auth:signIn'} defaults="SIGN IN" />
      </a>
    </div>
  );
}

function useSession() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();

      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';

      try {
        // Check if user has account membership (owner/member)
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);

        // If user has any membership with owner or member role, they have admin access
        if (memberships && memberships.length > 0) {
          if (memberships.some(m => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        // If authenticated but no account membership, they're a 'user'
        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        // Get user's team account membership
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(`
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `)
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);

        // Return the first team account
        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
